/*
 * Upload history & duplicate detection.
 *
 * Browser sandboxes prevent us from referencing photos by their library
 * ID (only native apps get that). The practical substitute: hash the
 * actual file bytes (SHA-256) when the user selects them, and remember
 * the hashes we've already successfully scanned. If a future selection
 * produces the same hash, we know it's a duplicate even if the user
 * renamed the file or re-saved it.
 *
 * The hash store lives in localStorage so it survives sessions but is
 * scoped per-device. If the user clears site data they get a fresh log
 * (no big deal — at worst they re-scan a screenshot).
 *
 * Limits:
 *   - We cap the store at 5000 entries (FIFO eviction). This is enough
 *     for years of normal use without bloating localStorage.
 *   - We never store the file contents — only the hash + filename + size
 *     + a millisecond timestamp.
 */

const STORAGE_KEY = 'groovlens.uploadedHashes.v1';
const MAX_ENTRIES = 5000;

function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function write(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (_) {
    /* localStorage quota / private mode — silently ignore */
  }
}

/**
 * SHA-256 the file bytes. Returns a 64-char hex string.
 *
 * Reading the entire file into memory is fine for screenshots (typically
 * < 5 MB each) and the crypto.subtle.digest call runs on a worker thread,
 * so this won't block scroll/UI even for batches of 50+.
 */
export async function hashFile(file) {
  if (!file || typeof file.arrayBuffer !== 'function') return null;
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (e) {
    // crypto.subtle requires HTTPS (we are) but might fail in really old
    // browsers. Fall back to a much weaker fingerprint than nothing.
    return `fallback:${file.name}-${file.size}-${file.lastModified}`;
  }
}

export function hasUploaded(hash) {
  if (!hash) return false;
  const map = read();
  return Boolean(map[hash]);
}

/**
 * Look up multiple hashes at once. Returns a Set of hashes that are
 * already in the store.
 */
export function findKnownHashes(hashes) {
  const map = read();
  const known = new Set();
  for (const h of hashes || []) {
    if (h && map[h]) known.add(h);
  }
  return known;
}

/**
 * Record a successful upload. Caller passes the hash + a meta payload
 * (filename, size, etc.) so the entry can be inspected later if needed.
 */
export function recordUploaded(hash, meta = {}) {
  if (!hash) return;
  const map = read();
  if (map[hash]) {
    // Already known — just bump the timestamp.
    map[hash] = { ...map[hash], ...meta, lastSeen: Date.now() };
  } else {
    map[hash] = {
      filename: meta.filename || null,
      size: typeof meta.size === 'number' ? meta.size : null,
      uploadedAt: Date.now(),
    };
  }

  // FIFO eviction if over the cap. JSON.parse preserves insertion order
  // on plain objects in all modern engines, so the first N entries are
  // the oldest.
  const keys = Object.keys(map);
  if (keys.length > MAX_ENTRIES) {
    const toDelete = keys.slice(0, keys.length - MAX_ENTRIES);
    for (const k of toDelete) delete map[k];
  }
  write(map);
}

export function count() {
  return Object.keys(read()).length;
}

export function clearAll() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* nope */ }
}

/* --------------------- Photo-cleanup nudge prefs --------------------- */

const CLEANUP_NEVER_SHOW_KEY = 'groovlens.cleanupNudge.neverShow.v1';

export function shouldShowCleanupNudge() {
  try {
    return localStorage.getItem(CLEANUP_NEVER_SHOW_KEY) !== '1';
  } catch (_) {
    return true;
  }
}

export function neverShowCleanupNudge() {
  try { localStorage.setItem(CLEANUP_NEVER_SHOW_KEY, '1'); } catch (_) { /* nope */ }
}

export function resetCleanupNudge() {
  try { localStorage.removeItem(CLEANUP_NEVER_SHOW_KEY); } catch (_) { /* nope */ }
}
